import React from 'react';

interface TimelineEntry {
    team_abbrev: string;
    team_name: string;
    conference: string;
    elimination_date: string | null;
    post_elim_points: number;
    post_elim_wins: number;
    post_elim_record: string;
    larry_pool_rank: number | null;
}

interface Props {
    timeline: TimelineEntry[];
    league: string;
    loading: boolean;
}

// Group eliminations by month for cleaner display
function groupByMonth(entries: TimelineEntry[]): Record<string, TimelineEntry[]> {
    const groups: Record<string, TimelineEntry[]> = {};
    for (const entry of entries) {
        if (!entry.elimination_date) continue;
        const d = new Date(entry.elimination_date + 'T00:00:00');
        const key = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        if (!groups[key]) groups[key] = [];
        groups[key].push(entry);
    }
    return groups;
}

const RANK_COLORS: Record<number, string> = {
    1: '#d4af37',  // gold
    2: '#9ca3af',  // silver
    3: '#cd7f32',  // bronze
};

const EliminationTimeline: React.FC<Props> = ({ timeline, league, loading }) => {
    if (loading || timeline.length === 0) return null;

    const isNHL = league === 'nhl';
    const unit = isNHL ? 'pts' : 'wins';
    const getLpValue = (t: TimelineEntry) => isNHL ? t.post_elim_points : t.post_elim_wins;

    // Sort by elimination date ascending
    const sorted = [...timeline].sort((a, b) => {
        if (!a.elimination_date) return 1;
        if (!b.elimination_date) return -1;
        return a.elimination_date.localeCompare(b.elimination_date);
    });

    const groups = groupByMonth(sorted);
    const monthKeys = Object.keys(groups);

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Find max LP points for bar scaling
    const maxLpPoints = Math.max(1, ...sorted.map(t => getLpValue(t)));

    return (
        <div className="standings-section">
            <h2>Elimination Timeline</h2>
            <p style={{ color: '#666', fontSize: '0.88rem', margin: '-6px 0 16px' }}>
                When each team was mathematically eliminated — and how many Larry Pool {unit} they earned after.
            </p>

            {monthKeys.map((month) => (
                <div key={month} style={{ marginBottom: 24 }}>
                    {/* Month header */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        marginBottom: 8
                    }}>
                        <div style={{
                            fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.08em', color: '#6b7280', padding: '2px 10px',
                            background: '#f3f4f6', borderRadius: 20
                        }}>
                            {month}
                        </div>
                        <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                    </div>

                    {/* Teams eliminated in this month */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {groups[month].map((team) => {
                            const rank = team.larry_pool_rank;
                            const rankColor = rank && RANK_COLORS[rank] ? RANK_COLORS[rank] : '#dc2626';
                            const lpValue = getLpValue(team);
                            const barPct = maxLpPoints > 0
                                ? Math.round((lpValue / maxLpPoints) * 100)
                                : 0;

                            return (
                                <div key={team.team_abbrev} style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '8px 12px',
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    borderLeft: `3px solid ${rankColor}`,
                                }}>
                                    {/* Rank badge */}
                                    <div style={{
                                        width: 28, height: 28, borderRadius: '50%',
                                        background: rank && rank <= 3 ? rankColor : '#f3f4f6',
                                        color: rank && rank <= 3 ? '#fff' : '#6b7280',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '0.75rem', fontWeight: 700, flexShrink: 0
                                    }}>
                                        {rank ? `#${rank}` : '—'}
                                    </div>

                                    {/* Team name + date */}
                                    <div style={{ minWidth: 140 }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                                            {team.team_name}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                            Eliminated {team.elimination_date ? formatDate(team.elimination_date) : '—'}
                                            {' · '}{team.conference}
                                        </div>
                                    </div>

                                    {/* LP points bar */}
                                    <div style={{ flex: 1, minWidth: 80 }}>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 8
                                        }}>
                                            <div style={{
                                                flex: 1, height: 6,
                                                background: '#f3f4f6', borderRadius: 3,
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${barPct}%`, height: '100%',
                                                    background: rankColor,
                                                    borderRadius: 3,
                                                    transition: 'width 0.3s ease'
                                                }} />
                                            </div>
                                            <span style={{
                                                fontSize: '0.85rem', fontWeight: 700,
                                                color: rankColor, minWidth: 30, textAlign: 'right'
                                            }}>
                                                {lpValue} {unit}
                                            </span>
                                        </div>
                                        {team.post_elim_record && (
                                            <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>
                                                Post-elim record: {team.post_elim_record}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default EliminationTimeline;
